import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { PageHeader, Card, Btn, Input, Select, Label, Modal, Empty, Badge, Table, Td } from "../components/UI";
import { Plus, Pencil, Trash2, Boxes } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", category: "", serial_number: "", status: "available", assigned_to_name: "", location: "",
  purchase_date: "", warranty_expires: "", next_maintenance: "", notes: "" };

export default function Equipment() {
  useEffect(() => { document.title = "Equipment | COCOA EHR"; }, []);
  const [rows, setRows] = useState([]);
  const [lists, setLists] = useState({ equipment_categories: [], equipment_statuses: [] });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [filter, setFilter] = useState("");

  const load = () => api.get("/equipment").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => {
    load();
    api.get("/custom-lists").then((r) => setLists(r.data)).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/equipment/${editId}`, form);
      else await api.post("/equipment", form);
      toast.success(editId ? "Equipment updated" : "Equipment added");
      setOpen(false); setForm(EMPTY); setEditId(null); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (r) => {
    try { await api.delete(`/equipment/${r.id}`); toast.success("Equipment removed"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const filtered = filter ? rows.filter((r) => r.status === filter) : rows;
  const f = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  return (
    <div data-testid="equipment-page">
      <PageHeader title="Equipment Management" subtitle="Track clinic assets, assignments & maintenance"
        action={<Btn data-testid="equipment-add-btn" onClick={() => { setForm({ ...EMPTY, category: lists.equipment_categories[0] || "Other" }); setEditId(null); setOpen(true); }}><Plus size={15} /> Add Equipment</Btn>} />
      <Card className="p-4 mb-4 flex flex-wrap items-center gap-2">
        <Boxes size={16} className="text-[#7B6157]" />
        <span className="text-sm font-semibold text-[#5D4037]">{rows.length} assets</span>
        <div className="ml-auto flex gap-1.5">
          <button data-testid="equipment-filter-all" onClick={() => setFilter("")} className={`rounded-full px-3 py-1 text-xs font-semibold ${!filter ? "bg-[#4E342E] text-white" : "bg-[#F5F2EB] text-[#6D4C41]"}`}>All</button>
          {(lists.equipment_statuses || []).map((s) => (
            <button key={s} data-testid={`equipment-filter-${s}`} onClick={() => setFilter(s)}
              className={`rounded-full px-3 py-1 text-xs font-semibold capitalize ${filter === s ? "bg-[#4E342E] text-white" : "bg-[#F5F2EB] text-[#6D4C41]"}`}>{s.replace("_", " ")}</button>
          ))}
        </div>
      </Card>
      {filtered.length === 0 ? <Empty text="No equipment recorded" /> : (
        <Card className="overflow-x-auto">
          <Table headers={["Name", "Category", "Serial #", "Status", "Assigned to", "Location", "Next maintenance", ""]}>
            {filtered.map((r) => (
              <tr key={r.id} className="border-b border-[#F1EEE7] hover:bg-[#FDFCF8]" data-testid={`equipment-row-${r.id}`}>
                <Td className="font-semibold text-[#3E2723]">{r.name}</Td>
                <Td>{r.category}</Td>
                <Td className="font-mono text-xs">{r.serial_number || "-"}</Td>
                <Td><Badge value={r.status} /></Td>
                <Td>{r.assigned_to_name || "-"}</Td>
                <Td>{r.location || "-"}</Td>
                <Td>{r.next_maintenance || "-"}</Td>
                <Td>
                  <div className="flex gap-1">
                    <button data-testid={`equipment-edit-${r.id}`} onClick={() => { setForm({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, r[k] || ""])) }); setEditId(r.id); setOpen(true); }}
                      className="p-1.5 rounded-lg text-[#7B6157] hover:bg-[#F5F2EB]"><Pencil size={14} /></button>
                    <button data-testid={`equipment-delete-${r.id}`} onClick={() => remove(r)} className="p-1.5 rounded-lg text-[#C62828] hover:bg-[#FFEBEE]"><Trash2 size={14} /></button>
                  </div>
                </Td>
              </tr>
            ))}
          </Table>
        </Card>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Edit Equipment" : "Add Equipment"}>
        <form onSubmit={save} className="space-y-4">
          <div><Label>Name</Label><Input data-testid="equipment-name-input" value={form.name} onChange={f("name")} required /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Category</Label>
              <Select data-testid="equipment-category-select" value={form.category} onChange={f("category")}>
                {(lists.equipment_categories || []).map((c) => <option key={c}>{c}</option>)}
              </Select></div>
            <div><Label>Status</Label>
              <Select data-testid="equipment-status-select" value={form.status} onChange={f("status")}>
                {(lists.equipment_statuses || []).map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
              </Select></div>
            <div><Label>Serial number</Label><Input data-testid="equipment-serial-input" value={form.serial_number} onChange={f("serial_number")} /></div>
            <div><Label>Assigned to</Label><Input data-testid="equipment-assigned-input" value={form.assigned_to_name} onChange={f("assigned_to_name")} placeholder="Staff member / room" /></div>
            <div><Label>Location</Label><Input value={form.location} onChange={f("location")} /></div>
            <div><Label>Purchase date</Label><Input type="date" value={form.purchase_date} onChange={f("purchase_date")} /></div>
            <div><Label>Warranty expires</Label><Input type="date" value={form.warranty_expires} onChange={f("warranty_expires")} /></div>
            <div><Label>Next maintenance</Label><Input type="date" value={form.next_maintenance} onChange={f("next_maintenance")} /></div>
          </div>
          <div><Label>Notes</Label><Input value={form.notes} onChange={f("notes")} /></div>
          <div className="flex justify-end gap-2">
            <Btn type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn>
            <Btn data-testid="equipment-save-btn" type="submit">{editId ? "Save Changes" : "Add Equipment"}</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
